import { useEffect, useState } from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ASSUNTOS_AVISO, ROTULO_ASSUNTO, ROTAS, type AssuntoAviso } from '@remoto/shared';
import { rotaPainel } from './painel';
import { chamar } from '../api';
import { BotaoLeve, Cartao } from '../componentes/layout';
import { CaixaDeMarcar } from '../componentes/campos';
import { usarConfirmacao } from '../componentes/Modal';
import { Tela } from '../workspace/Tela';
import { tempoRelativo } from '../formato';
import {
  type EstadoDoAviso,
  desligarAvisos,
  estadoDoAviso,
  ligarAvisos,
} from '../notificacoes/push';

interface Aparelho {
  id: string;
  apelido: string | null;
  criadoEm: string;
  usadoEm: string | null;
}

/**
 * Ligar e desligar os avisos deste navegador.
 *
 * Este cartão morava na página de Saúde, que só administrador enxerga — e foi
 * essa a razão de o tablet nunca ter recebido nada: a pessoa ligou os avisos no
 * computador e não tinha por onde ligar no aparelho. Aviso é assunto de quem
 * recebe, não de quem administra.
 */
function NesteAparelho(): React.JSX.Element {
  const [estado, setEstado] = useState<EstadoDoAviso | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [recado, setRecado] = useState('');
  const cliente = useQueryClient();

  useEffect(() => {
    void estadoDoAviso().then(setEstado);
  }, []);

  async function alternar(): Promise<void> {
    setOcupado(true);
    setRecado('');
    try {
      setEstado(estado === 'ligado' ? await desligarAvisos() : await ligarAvisos());
      void cliente.invalidateQueries({ queryKey: ['aparelhos-de-aviso'] });
    } catch {
      setRecado('Não consegui mudar isso agora.');
    } finally {
      setOcupado(false);
    }
  }

  async function testar(): Promise<void> {
    setRecado('');
    try {
      await chamar(ROTAS.notificacoes.teste, { method: 'POST' });
      setRecado('Enviado. Deve aparecer em instantes — inclusive com o painel fechado.');
    } catch {
      setRecado('O envio de teste falhou.');
    }
  }

  return (
    <Cartao titulo="Avisos neste aparelho">
      {estado === 'indisponivel' ? (
        <p className="text-sm text-zinc-500">
          Este navegador não recebe avisos. No iPhone e no iPad é preciso adicionar o painel à tela
          de início e abrir por lá antes.
        </p>
      ) : estado === 'negado' ? (
        <p className="text-sm text-amber-300">
          Você bloqueou os avisos para este site. Para voltar atrás é preciso liberar nas
          configurações do navegador — a página não consegue pedir de novo.
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-3">
          <BotaoLeve onClick={() => void alternar()} disabled={ocupado || estado === null}>
            {estado === 'ligado' ? 'Desligar aqui' : 'Ligar avisos aqui'}
          </BotaoLeve>
          {estado === 'ligado' ? (
            <BotaoLeve onClick={() => void testar()}>Mandar um teste</BotaoLeve>
          ) : null}
          <span className="text-xs text-zinc-500">
            {estado === 'ligado'
              ? 'Este aparelho está inscrito. O teste é a prova: se ele chega, o resto chega.'
              : 'Sem avisos, um pedido de permissão fica esperando em silêncio.'}
          </span>
        </div>
      )}
      {recado ? <p className="mt-3 text-sm text-zinc-400">{recado}</p> : null}
    </Cartao>
  );
}

/**
 * Os aparelhos inscritos.
 *
 * Existe para responder "por que não chega no tablet?" sem adivinhação: se o
 * aparelho não está nesta lista, ele não está inscrito, ponto. O `usadoEm` diz
 * quando o servidor conseguiu entregar algo por ali pela última vez.
 */
function Aparelhos(): React.JSX.Element {
  const cliente = useQueryClient();
  const [confirmar, dialogo] = usarConfirmacao();

  const { data: aparelhos = [], isLoading } = useQuery({
    queryKey: ['aparelhos-de-aviso'],
    // A lista é o que a tela desenha: uma resposta fora do formato — proxy que
    // devolveu erro em JSON, rota nova ainda sem servidor — não pode virar tela
    // branca no meio da página de avisos.
    queryFn: async () => {
      const dados = await chamar<Aparelho[]>(ROTAS.notificacoes.inscricoes);
      return Array.isArray(dados) ? dados : [];
    },
  });

  const remover = useMutation({
    mutationFn: (id: string) =>
      chamar(`${ROTAS.notificacoes.inscricoes}/${id}`, { method: 'DELETE' }),
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['aparelhos-de-aviso'] }),
  });

  return (
    <Cartao titulo="Aparelhos inscritos">
      {dialogo}
      {isLoading ? <p className="text-sm text-zinc-500">carregando…</p> : null}
      {!isLoading && aparelhos.length === 0 ? (
        <p className="text-sm text-zinc-500">
          Nenhum aparelho inscrito ainda. Abra o painel no aparelho que deve avisar e ligue por lá.
        </p>
      ) : null}
      <ul className="space-y-2">
        {aparelhos.map((a) => (
          <li key={a.id} className="flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="truncate text-sm text-zinc-200" title={a.apelido ?? ''}>
                {a.apelido ?? 'aparelho sem nome'}
              </p>
              <p className="text-xs text-zinc-500">
                inscrito {tempoRelativo(a.criadoEm, 'há pouco')} · último aviso{' '}
                {tempoRelativo(a.usadoEm, 'nenhum ainda')}
              </p>
            </div>
            <BotaoLeve
              onClick={() =>
                confirmar({
                  titulo: 'Remover aparelho',
                  texto: `${a.apelido ?? 'Este aparelho'} deixa de receber avisos. Para voltar, é só ligar de novo nele.`,
                  rotuloConfirmar: 'Remover',
                  perigosa: true,
                  aoConfirmar: () => remover.mutate(a.id),
                })
              }
            >
              Remover
            </BotaoLeve>
          </li>
        ))}
      </ul>
    </Cartao>
  );
}

/** O que merece interromper você, escolhido por assunto. */
function OQueMeAvisa(): React.JSX.Element {
  const cliente = useQueryClient();
  const [erro, setErro] = useState('');

  /**
   * A resposta vem embrulhada em `{ assuntos }`, e o corpo do PUT também.
   *
   * Ler o objeto cru como se fosse o mapa deixava toda caixa desmarcada e todo
   * clique reprovado na validação da borda — a tela ficava sem jeito de ligar
   * nada. O tipo aqui é a forma do servidor, de propósito: foi confiar numa
   * forma imaginada que produziu o defeito.
   */
  const { data } = useQuery({
    queryKey: ['preferencias-de-aviso'],
    queryFn: () =>
      chamar<{ assuntos: Record<AssuntoAviso, boolean> }>(ROTAS.notificacoes.preferencias),
  });
  const assuntos = data?.assuntos;

  const salvar = useMutation({
    // Só o que mudou: o servidor mescla com o que já estava salvo.
    mutationFn: (novos: Partial<Record<AssuntoAviso, boolean>>) =>
      chamar(ROTAS.notificacoes.preferencias, { method: 'PUT', corpo: { assuntos: novos } }),
    onMutate: (novos) => {
      const antes = cliente.getQueryData<{ assuntos: Record<AssuntoAviso, boolean> }>([
        'preferencias-de-aviso',
      ]);
      if (antes) {
        cliente.setQueryData(['preferencias-de-aviso'], {
          assuntos: { ...antes.assuntos, ...novos },
        });
      }
      return { antes };
    },
    onError: (_e, _v, contexto) => {
      if (contexto?.antes) cliente.setQueryData(['preferencias-de-aviso'], contexto.antes);
      setErro('Não consegui salvar essa escolha.');
    },
    onSettled: () => void cliente.invalidateQueries({ queryKey: ['preferencias-de-aviso'] }),
  });

  // `teste` não entra: ele sempre passa, porque é o botão que a pessoa aperta
  // justamente para descobrir se os avisos funcionam neste aparelho.
  const visiveis = ASSUNTOS_AVISO.filter((a) => a !== 'teste');

  return (
    <Cartao titulo="O que me avisa">
      <p className="mb-4 text-xs text-zinc-500">
        Vale para a sua conta, em todos os aparelhos. Transcrição de gravação vem desligada de
        propósito: o ditado do compositor usa a mesma esteira, e avisar a cada frase ditada só
        ensina a ignorar aviso.
      </p>
      <ul className="space-y-2">
        {visiveis.map((assunto) => {
          const ligado = assuntos?.[assunto] ?? false;
          return (
            <li key={assunto}>
              <CaixaDeMarcar
                marcada={ligado}
                desabilitada={!assuntos}
                className="text-sm text-zinc-300"
                aoMarcar={(marcada) => {
                  setErro('');
                  salvar.mutate({ [assunto]: marcada });
                }}
              >
                {ROTULO_ASSUNTO[assunto]}
              </CaixaDeMarcar>
            </li>
          );
        })}
      </ul>
      {erro ? <p className="mt-3 text-sm text-amber-300">{erro}</p> : null}
    </Cartao>
  );
}

function Notificacoes(): React.JSX.Element {
  return (
    <Tela>
      <NesteAparelho />
      <Aparelhos />
      <OQueMeAvisa />
    </Tela>
  );
}

export const rotaNotificacoes = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/notificacoes',
  component: Notificacoes,
});
